import { useState } from 'react';
import { Button, Input } from '@dycs/ui';
import { callableMessage, escalate } from '@/lib/firebase';

/**
 * 「칭찬 릴레이」 접수 폼 (2026-10-01 way · 온세미 구글폼 «칭찬릴레이 접수» 이관).
 *   칸은 원 폼 그대로 — 사번(필수) · 노선(필수) · 칭찬 기사명(선택) · 칭찬 내용(필수).
 *   🔴 이름·연락처를 받지 않는다. 회신할 곳이 없는 접수라 완료 화면도 «회신드리겠습니다»를 쓰지 않는다.
 *   필수 판정은 서버(ticketCore)가 한 번 더 한다 — 여기는 안내용.
 */
interface PraiseFormProps {
  tenantId: string;
  routes: string[];
}

const ROUTE_ETC = '__etc__';

export function PraiseForm({ tenantId, routes }: PraiseFormProps) {
  const [employeeNo, setEmployeeNo] = useState('');
  const [route, setRoute] = useState('');
  const [routeFree, setRouteFree] = useState('');
  const [driverName, setDriverName] = useState('');
  const [content, setContent] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const hasRouteList = routes.length > 0;
  const routeFreeShown = !hasRouteList || route === ROUTE_ETC;
  const routeValue = (routeFreeShown ? routeFree : route).trim();

  if (done) {
    return (
      <div className="text-center py-6 space-y-2">
        <div className="text-2xl">👏</div>
        <p className="text-sm font-medium">칭찬이 접수되었습니다.</p>
        <p className="text-xs text-text-tertiary">
          접수번호 <span className="font-mono text-text-secondary">{done.slice(0, 8)}</span>
        </p>
        <p className="text-xs text-text-tertiary">소중한 칭찬은 기사님께 꼭 전달하겠습니다. 감사합니다.</p>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (!employeeNo.trim()) {
      setError('사번을 입력해 주세요.');
      return;
    }
    if (!routeValue) {
      setError(routeFreeShown ? '노선을 입력해 주세요.' : '노선을 선택해 주세요.');
      return;
    }
    if (!content.trim()) {
      setError('칭찬 내용을 입력해 주세요.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await escalate({
        tenantId,
        type: 'inquiry',
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        content: content.trim(),
        route: routeValue,
        employeeNo: employeeNo.trim(),
        category: '칭찬',
        praiseRelay: true,
        ...(driverName.trim() ? { praiseDriverName: driverName.trim() } : {}),
        privacyConsent: true,
      });
      setDone(res.ticketId);
    } catch (err) {
      console.error('[widget] 칭찬 릴레이 접수 실패', err);
      setError(callableMessage(err, '접수에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <Input
        label="사번"
        inputMode="text"
        placeholder="예: 202100386"
        required
        value={employeeNo}
        onChange={(e) => setEmployeeNo(e.target.value)}
      />

      <div>
        <span className="block mb-1 text-xs font-medium text-text-secondary">
          노선<span className="text-danger"> *</span>
        </span>
        {hasRouteList && (
          <select
            className="w-full px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none"
            value={route}
            onChange={(e) => setRoute(e.target.value)}
            required
          >
            <option value="">선택해 주세요</option>
            {routes.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
            <option value={ROUTE_ETC}>기타 (직접 입력)</option>
          </select>
        )}
        {routeFreeShown && (
          <input
            type="text"
            className={`w-full px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none placeholder:text-text-disabled${
              hasRouteList ? ' mt-2' : ''
            }`}
            placeholder="예: 인천 B 출근버스"
            value={routeFree}
            onChange={(e) => setRouteFree(e.target.value)}
            required
          />
        )}
      </div>

      <Input
        label="칭찬 기사명 (선택)"
        placeholder="예: 홍길동 기사님"
        value={driverName}
        onChange={(e) => setDriverName(e.target.value)}
      />

      <label className="block">
        <span className="block mb-1 text-xs font-medium text-text-secondary">
          칭찬 내용<span className="text-danger"> *</span>
        </span>
        <textarea
          className="w-full px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none min-h-24"
          placeholder="언제, 어떤 점이 좋았는지 적어 주세요."
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
        <span className="mt-1 block text-xs text-text-tertiary">
          최대한 자세히 적어 주시면(노선명·시간 등) 기사님을 찾아 전달하는 시간이 줄어듭니다.
        </span>
      </label>

      <label className="flex items-start gap-2 text-xs text-text-secondary">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>개인정보(사번) 수집·이용에 동의합니다. (칭찬 접수·확인 목적)</span>
      </label>

      {error && <p className="text-xs text-danger">{error}</p>}

      <Button full type="submit" disabled={!consent} loading={submitting}>
        칭찬 보내기
      </Button>
    </form>
  );
}
